import {ChangeDetectionStrategy, Component, computed, Injectable, input, output} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AgendaelementId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {TittelKomponent, TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDragHandle} from "@angular/cdk/drag-drop";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erFavorittHosBruker} from "felles-ng2/modell/brukerModellHjelpere";
import {NgTemplateOutlet} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {VarighetMinutterVelgerKomponent} from "web-ng2/delt/komponenter/varighet-minutter-velger/varighet-minutter-velger";
import {SaksvarighetKontroll} from "web-ng2/moduler/sak/komponenter/saksliste/saksliste";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {map, switchMap} from "rxjs/operators";
import {antallDokumenterLest, DokumenterInformasjon} from "felles-ng2/modell/dokumentModellHjelpere";
import {Observable} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Sakstidspunkt} from "felles-ng2/tjenester/Sakstidspunkter";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {antallOppgaverForfalt, OppgaverInformasjon} from "felles-ng2/modell/oppgaveModellHjelpere";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {MatTooltip} from "@angular/material/tooltip";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TemporalFormatering} from "felles-ng2/tjenester/TemporalFormatering";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {sakStartOgVarighetBeskrivelse} from "felles-ng2/modell/sakModellHjelpere";
import {IkonMedTellerKomponent} from "web-ng2/delt/komponenter/ikon-med-teller/ikon-med-teller";
import {ValgtSakRegister} from "web-ng2/tjenester/ValgtSakRegister";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {ScrollTilSynligDirektiv} from "web-ng2/delt/direktiver/scrollTilSynlig/scrollTilSynlig";

const debug = initDebugger('SakListevisningKomponent');


@Component({
    selector: 'sak-listevisning',
    templateUrl: 'sak-listevisning.html',
    styleUrl: 'sak-listevisning.scss',
    host: {
        'data-testid': 'sak',
        '[class.er-pause]': 'sak().erPause',
        '[class.aktiv]': 'erAktiv()',
        '[class.sortering-aktivert]': 'sorteringAktivert()',
        '[class.kompakt]': 'kompakt()',
        '[class.kompakt--ekstra]': 'ekstraKompakt()',
        '(click)': 'gaaTilSaksdetaljer()'
    },
    imports: [
        CdkDragHandle,
        ReactiveFormsModule,
        MatIcon,
        MatIconButton,
        NgTemplateOutlet,
        FormaterPlainTimePipe,
        FormaterDurationPipe,
        ZoneddatetimeSomPlainTimePipe,
        AvatarKomponent,
        TittelVerdiSamlingKomponent,
        TittelVerdiKomponent,
        TittelKomponent,
        VerdiKomponent,
        TranslateModule,
        VarighetMinutterVelgerKomponent,
        KlikkMedStopPropagationDirektiv,
        MatTooltip,
        IkonMedTellerKomponent,
        ScrollTilSynligDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakListevisningKomponent {

    vedHandling = output<RadHandling>();

    sak = input.required<AgendaelementModellDLM>();
    tidspunkt = input.required<Sakstidspunkt>();
    varighetKontroll = input<SaksvarighetKontroll | null>(null);
    /** Aktiverer kompakt visning - da vises kun saksnummer, sakstittel og klokkeslett/varighet.
     * For bruk når saksdetaljer-visning er aktivert i sak-liste-og-detaljer.
     * Merk at kompakt visning også aktiveres via @media query i css ved smalt nettleservindu */
    kompakt = input(false);
    /** Aktiverer ekstra kompakt visning - da vises kun saksnummer og klokkeslett/varighet.
     * Betinger at også kompakt=true.
     * For bruk når bla presentasjonsmodus er aktivert */
    ekstraKompakt = input(false);
    /** Slik at omverden kan aktivere sorteringsmoduset */
    sorteringAktivert = input(false);
    utenSortering = input(false);
    /** Når saken er aktiv scrolles den til synlig på nytt hver gang denne verdien endres.
     * Benyttes av saksliste (med antall rader) slik at aktiv sak holdes synlig når øvrige saker lastes inn etter at sak er valgt (f.eks. ved dyp lenke),
     * da blir aktiv sak initielt vist alene øverst i listen og deretter skjøvet nedover når resten av sakene kommer. */
    holdSynligVedEndringAv = input<unknown>();

    erFavoritt = computed(() => erFavorittHosBruker(this.sak().favoritter ?? [], this.kontekst));
    erAktiv = computed(() => this.valgtSakRegister.valgt() == this.sak().id);
    /** tooltip-tekst for .tid elementet, vises ikke når vi er i kompakt-modus da tooltipen er så lang at den dekker over underliggende sak og dermed forstyrrer muligheten til å klikke på denne */
    hjelpetekstTidspunkt = computed(() => !this.kompakt() ? sakStartOgVarighetBeskrivelse(this.tidspunkt(), this.oversettelser, this.gjeldendeTidssone, this.temporalFormatering) : '');
    antallDokumenter = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallDokumenter(sak.id))), {initialValue: {antallUleste: 0, antallTotalt: 0}});
    antallOppgaver = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallOppgaver(sak.id))), {initialValue: {antallForfalt: 0, antallTotalt: 0}});
    antallBeslutninger = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallBeslutninger(sak.id))), {initialValue: 0});
    antallKommentarer = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallKommentarer(sak.id))), {initialValue: 0});

    constructor(private avhengigheter: SakListevisningKomponentAvhengigheter,
                private valgtSakRegister: ValgtSakRegister,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjon: Navigasjon,
                private gjeldendeTidssone: GjeldendeTidssone,
                private oversettelser: Oversettelser,
                private temporalFormatering: TemporalFormatering) {
    }

    gaaTilSaksdetaljer() {
        if (this.sorteringAktivert()) return;
        if (this.erAktiv())
            this.navigasjon.gåTilMøte(this.sak().moete.id);
        else
            this.navigasjon.gåTilMøteOgSak(this.sak().moete.id, this.sak().id);
    }

    flyttOpp() {
        this.vedHandling.emit(RadHandling.flyttOpp);
    }

    flyttNed() {
        this.vedHandling.emit(RadHandling.flyttNed);
    }

    //private hjelpemetoder

}

export enum RadHandling {
    flyttOpp, flyttNed
}

@Injectable()
export class SakListevisningKomponentAvhengigheter {

    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private oppgaveDatatjeneste: OppgaveDatatjeneste,
                private beslutningDatatjeneste: BeslutningDatatjeneste,
                private kommentarDatatjeneste: KommentarDatatjeneste,
                private klokke: Klokke) {
    }

    antallDokumenter(sakId: AgendaelementId): Observable<DokumenterInformasjon> {
        return this.saksdokumentDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(dokumenter => antallDokumenterLest(dokumenter)));
    }

    antallOppgaver(sakId: AgendaelementId): Observable<OppgaverInformasjon> {
        return this.oppgaveDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(oppgaver => antallOppgaverForfalt(oppgaver, this.klokke)));
    }

    antallBeslutninger(sakId: AgendaelementId): Observable<number> {
        return this.beslutningDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(beslutninger => beslutninger.length));
    }

    antallKommentarer(sakId: AgendaelementId): Observable<number> {
        return this.kommentarDatatjeneste.finnTilknyttetFraLokallager(sakId)
          .pipe(map(kommentarer => kommentarer.length));
    }

}
